import React, { useCallback, useRef } from 'react';
import { useParams } from 'react-router-dom';
import SubPage from '#/common/components/SubPage';
import type { Expense } from '#/graphql/types';
import useExpenseQuery from '../hooks/useExpenseQuery';
import useNotFoundGuard from '#/common/hooks/useNotFoundGuard';
import RecordNotFound from '#/app/pages/errors/RecordNotFound';
import ExpenseForm from '../components/ExpenseForm';
import useExpenseUpdate from '../hooks/useExpenseUpdate';
import useExpenseDelete from '../hooks/useExpenseDelete';
import fields from '../components/ExpenseForm/fields';
import getFormTitle from '#/common/utils/getFormTitle';
import { SubmitExpense } from '../utils/types';

export interface ExpenseParams {
  readonly id?: string;
}

const ExpensePage: React.FC = () => {
  const { id } = useParams() as ExpenseParams;
  const expenseQuery = useExpenseQuery(id);
  const expense = expenseQuery.data?.getExpense?.data;
  const isNotFound = useNotFoundGuard({ loading: expenseQuery.loading, id, entity: expense });

  // Use a ref to store the initial updatedAt value to prevent form remount
  // on background refetches (cache-and-network). Only updates after user's own save.
  const formKeyRef = useRef<string | null>(null);
  if (expense && !formKeyRef.current) {
    formKeyRef.current = expense.updatedAt ?? expense.id ?? null;
  }

  const expenseNumber = expense?.displayNumber;
  const title = getFormTitle(id, expenseNumber, 'Expense');

  // Update Operation
  const { updateExpense } = useExpenseUpdate();
  const submit = useCallback<SubmitExpense>(
    async (expense: Expense) => {
      if (!id) {
        return undefined;
      }

      const { data, errors } = (await updateExpense(id, expense)) as {
        data?: Expense;
        errors?: string[];
      };

      if (data?.updatedAt && !errors?.length) {
        formKeyRef.current = data.updatedAt;
      }

      return {
        data: data ?? undefined,
        errors: errors ?? undefined,
      };
    },
    [id, updateExpense],
  );

  if (isNotFound) {
    return (
      <SubPage title={title} isLoading={false} showGoBackButton type="form">
        <RecordNotFound backUrl="/expenses" />
      </SubPage>
    );
  }

  return (
    <SubPage
      title={title || 'Expense'}
      isLoading={expenseQuery.loading}
      showGoBackButton
      type="form">
      {expense && (
        <ExpenseForm
          key={`expense-${id}-${formKeyRef.current}`}
          title={title}
          expense={expense}
          fields={fields}
          submit={submit}
        />
      )}
    </SubPage>
  );
};

export default React.memo(ExpensePage);
